import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { CurrencyObject, DateFormatObject } from 'common/types';
import React from 'react';
import { TFunction } from 'react-i18next';
import { ProductsTableRow } from './rows';
import productImagePlaceholder from 'common/assets/product-placeholder.png';
import { Stack, Avatar, Typography } from '@mui/material';
import { getPricingString } from 'common/utils/common';
import { localFormat } from 'common/utils/dateUtils';
import moment from 'moment';
import Tag from 'components/Tag';

interface ProductsTableColDef extends GridColDef {
	field: keyof ProductsTableRow;
	renderCell?: (params: GridRenderCellParams<any, ProductsTableRow>) => React.ReactNode;
}

export const getProductsTableColumns = (deps: {
	t: TFunction;
	currency: CurrencyObject;
	dateFormat: DateFormatObject;
	showDeposit: boolean;
}) => {
	const { t, currency, dateFormat, showDeposit } = deps;

	const columns: ProductsTableColDef[] = [
		{
			field: 'name',
			headerName: t('product.product', 'Product'),
			renderCell: ({ row }) => {
				return (
					<Stack direction="row" alignItems="center">
						<Avatar
							src={row.image || productImagePlaceholder}
							alt={row.name}
							sx={{ width: 80, height: 80 }}
							imgProps={{
								style: {
									objectFit: 'contain',
								},
							}}
							variant="square"
						/>
						<Typography sx={{ ml: 2, fontSize: '1.4rem', fontWeight: 500 }}>{row.name}</Typography>
					</Stack>
				);
			},
			minWidth: 400,
		},
		{
			field: 'status',
			headerName: t('product.status', 'Status'),
			valueFormatter: ({ value }) => {
				return value === 'public'
					? t('common:states.public', 'Public')
					: t('common:states.hidden', 'Hidden');
			},
			renderCell: ({ row }) => {
				return row.status === 'public' ? (
					<Tag
						color="success"
						text={t('common:states.public', 'Public')}
						tooltip={t('product.publicDescription', 'This product is visible to your customers')}
					/>
				) : (
					<Tag
						color="secondary"
						text={t('common:states.hidden', 'Hidden')}
						tooltip={t(
							'product.hiddenDescription',
							'This product is not visible to your customers',
						)}
					/>
				);
			},
		},
		{
			field: 'tags',
			headerName: t('product.tags', 'Tags'),
			valueGetter: ({ row }: { row: ProductsTableRow }) => {
				return row.tags.map((t) => t.text).join(', ');
			},
			renderCell: ({ row }: { row: ProductsTableRow }) => {
				return (
					<Stack direction="row" spacing={1}>
						{row.tags.map((tag) => (
							<Tag key={tag.text} {...tag} />
						))}
					</Stack>
				);
			},
			minWidth: 200,
		},
		{
			field: 'categories',
			headerName: t('product.categories', 'Categories'),
			minWidth: 300,
		},
		{
			field: 'channels',
			headerName: t('common:salesChannel.plural'),
			minWidth: 250,
		},
		{
			field: 'price',
			headerName: t('product.priceFromWithCurrency', {
				currencySymbol: currency.displayAs,
				defaultValue: 'Price from ({{currencySymbol}})',
			}),
			valueFormatter: ({ value }) => {
				if (value === 0) return '-';
				return getPricingString(value as number);
			},
			filterable: false,
			minWidth: 150,
		},
		{
			field: 'deposit',
			headerName: t('product.depositWithCurrency', {
				currencySymbol: currency.displayAs,
				defaultValue: 'Deposit ({{currencySymbol}})',
			}),
			valueFormatter: ({ value }) => {
				return getPricingString(value as number);
			},
			filterable: false,
			minWidth: 150,
		},
		{
			field: 'created',
			headerName: t('common:times.created', 'Created'),
			valueFormatter: ({ value }) => {
				return !!value
					? localFormat(moment(value as string).toISOString(), 'DD.MM.YYYY', dateFormat)
					: '';
			},
			minWidth: 200,
		},
	];

	return columns.filter((col) => {
		if (col.field === 'deposit' && !showDeposit) return false;
		return true;
	});
};
